// Whether a listing can still be bought, as buyers and search engines see it (BSC-15).
//
// A listing the seller closed (including vacation mode, which closes every listing and reopens
// them later) is "closed": the page says "No longer available". A published listing with no stock
// left is "sold": the page says "Sold". Both stay reachable with a 200 and full details, are
// marked SoldOut in the Product schema, and are left out of the sitemap (server/sitemap). Only a
// deleted or never-existing listing is "not found" (a real 404).
import { LISTING_STATE_CLOSED } from './types';

export const LISTING_AVAILABLE = 'available';
export const LISTING_SOLD = 'sold';
export const LISTING_CLOSED = 'closed';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * @param {Object} listing denormalised listing, with currentStock when stock is in use
 * @returns {String} LISTING_AVAILABLE, LISTING_SOLD or LISTING_CLOSED
 */
export const listingAvailability = listing => {
  if (listing?.attributes?.state === LISTING_STATE_CLOSED) {
    return LISTING_CLOSED;
  }
  // Same rule as the buy button: only a known zero counts as sold
  return listing?.currentStock?.attributes?.quantity === 0 ? LISTING_SOLD : LISTING_AVAILABLE;
};

/**
 * Product schema availability for a listing's availability.
 */
export const schemaAvailability = availability =>
  availability === LISTING_AVAILABLE ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut';

/**
 * Whether a listing id from the URL is well formed. Sharetribe answers a malformed id with a 400
 * validation error rather than a 404.
 */
export const isWellFormedListingId = id => typeof id === 'string' && UUID_PATTERN.test(id);

/**
 * Whether a failed listing fetch means the listing does not exist: deleted or never existing
 * (404), or an id that cannot be a listing (400 on a malformed id). These get the not found page
 * and a real 404 status instead of the error page with a 200.
 */
export const isListingNotFoundError = (error, listingId) =>
  !!error && (error.status === 404 || (error.status === 400 && !isWellFormedListingId(listingId)));
